import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { db } from '../firebase';
import { collection, query, getDocs, where, orderBy } from 'firebase/firestore';
import PageHeader from '../components/PageHeader';
import AnimatedSection from '../components/AnimatedSection';
import SEO from '../components/SEO';
import { FaExternalLinkAlt, FaSearch, FaImages, FaCalendarAlt, FaTimes } from 'react-icons/fa';
import '../components/styles.css';

const GaleriPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [albums, setAlbums] = useState([]); // Renamed from groupedImages for clarity
  const [categories, setCategories] = useState([]); // New state for categories
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedYear, setSelectedYear] = useState('Semua Tahun');
  const [selectedCategory, setSelectedCategory] = useState('Semua');
  const [selectedAlbumForModal, setSelectedAlbumForModal] = useState(null);

  const handleAlbumClick = (album) => {
    const links = Array.isArray(album.externalLinks) && album.externalLinks.length > 0
      ? album.externalLinks.filter(l => l.url && l.url.trim() !== '')
      : album.externalLink
        ? [{ label: 'Google Photos', url: album.externalLink }]
        : [];

    if (links.length === 0) {
      alert('Link album belum tersedia.');
    } else if (links.length === 1) {
      window.open(links[0].url, '_blank', 'noopener,noreferrer');
    } else {
      setSelectedAlbumForModal({ ...album, links });
    }
  };

  const fetchGalleryData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // 1. Fetch visible categories (Albums)
      const albumsQuery = query(collection(db, 'galeri_kategori'), where("isVisible", "==", true));
      const albumsSnapshot = await getDocs(albumsQuery);

      const loadedAlbums = albumsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // 2. Client-side sorting (Newest first)
      loadedAlbums.sort((a, b) => {
        const dateA = a.date ? new Date(a.date) : new Date(0);
        const dateB = b.date ? new Date(b.date) : new Date(0);
        return dateB - dateA;
      });

      setAlbums(loadedAlbums);

      // 3. Fetch Album Categories
      const catQuery = query(collection(db, 'album_categories'), orderBy('name', 'asc'));
      const catSnapshot = await getDocs(catQuery);
      setCategories(catSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))); // Store categories

    } catch (error) {
      console.error("Error fetching gallery data: ", error);
      setError("Gagal memuat galeri. Silakan coba lagi nanti.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGalleryData();
  }, [fetchGalleryData]);

  // Extract years from albums
  const availableYears = ['Semua Tahun', ...new Set(
    albums
      .map(album => album.date ? new Date(album.date).getFullYear().toString() : null)
      .filter(year => year !== null)
  )].sort((a, b) => b - a);

  // Filter logic
  const filteredAlbums = albums.filter((album) => {
    const matchesSearch = album.name.toLowerCase().includes(searchQuery.toLowerCase());
    const albumYear = album.date ? new Date(album.date).getFullYear().toString() : null;
    const matchesYear = selectedYear === 'Semua Tahun' || albumYear === selectedYear;
    // New: Category Filter
    const matchesCategory = selectedCategory === 'Semua' ||
      (selectedCategory === 'Umum' && (!album.category || album.category === 'Umum')) ||
      album.category === selectedCategory;

    return matchesSearch && matchesYear && matchesCategory;
  });

  return (
    <>
      <SEO
        title="Galeri Sekolah - SMK Pariwisata Sumbangsih"
        description="Dokumentasi foto kegiatan siswa, fasilitas, dan acara-acara penting di lingkungan SMK Pariwisata Sumbangsih."
      />
      <PageHeader title="Galeri Sekolah" subtitle="Dokumentasi kegiatan dan momen terbaik sekolah kami." />

      <AnimatedSection>
        <section className="section" style={{ background: '#f8fafc', minHeight: '60vh' }}>
          <div className="container">

            {/* Controls Bar */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '20px',
              marginBottom: '40px',
              background: '#fff',
              padding: '20px',
              borderRadius: '12px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
            }}>
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '16px',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%'
              }}>
                <div style={{ position: 'relative', flex: '1 1 300px' }}>
                  <FaSearch style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                  <input
                    type="text"
                    placeholder="Cari album kegiatan..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px 12px 12px 40px',
                      borderRadius: '8px',
                      border: '1px solid #e2e8f0',
                      fontSize: '0.95rem',
                      outline: 'none',
                      transition: 'border-color 0.2s'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  />
                </div>

                <div style={{ minWidth: '150px' }}>
                  <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px',
                      borderRadius: '8px',
                      border: '1px solid #e2e8f0',
                      fontSize: '0.95rem',
                      cursor: 'pointer',
                      backgroundColor: '#fff'
                    }}
                  >
                    {availableYears.map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Category Filter Tabs */}
              <div style={{
                display: 'flex',
                gap: '10px',
                overflowX: 'auto',
                paddingBottom: '5px',
                width: '100%',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none'
              }}>
                <button
                  onClick={() => setSelectedCategory('Semua')}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '20px',
                    border: '1px solid',
                    borderColor: selectedCategory === 'Semua' ? '#3b82f6' : '#e2e8f0',
                    background: selectedCategory === 'Semua' ? '#eff6ff' : '#fff',
                    color: selectedCategory === 'Semua' ? '#2563eb' : '#64748b',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    fontWeight: '600',
                    fontSize: '0.9rem',
                    transition: 'all 0.2s'
                  }}
                >
                  Semua
                </button>
                <button
                  onClick={() => setSelectedCategory('Umum')}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '20px',
                    border: '1px solid',
                    borderColor: selectedCategory === 'Umum' ? '#3b82f6' : '#e2e8f0',
                    background: selectedCategory === 'Umum' ? '#eff6ff' : '#fff',
                    color: selectedCategory === 'Umum' ? '#2563eb' : '#64748b',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    fontWeight: '600',
                    fontSize: '0.9rem',
                    transition: 'all 0.2s'
                  }}
                >
                  Umum
                </button>
                {categories.map(cat => (
                  <button
                    key={cat.id}
                    onClick={() => setSelectedCategory(cat.name)}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '20px',
                      border: '1px solid',
                      borderColor: selectedCategory === cat.name ? '#3b82f6' : '#e2e8f0',
                      background: selectedCategory === cat.name ? '#eff6ff' : '#fff',
                      color: selectedCategory === cat.name ? '#2563eb' : '#64748b',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      fontWeight: '600',
                      fontSize: '0.9rem',
                      transition: 'all 0.2s'
                    }}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Content Area */}
            {loading ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '30px' }}>
                {[1, 2, 3].map(i => (
                  <div key={i} style={{ height: '320px', background: '#e2e8f0', borderRadius: '16px', animation: 'pulse 2s infinite' }}></div>
                ))}
              </div>
            ) : error ? (
              <div style={{ textAlign: 'center', padding: '60px', color: '#ef4444' }}>
                <p>{error}</p>
              </div>
            ) : filteredAlbums.length > 0 ? (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                gap: '30px'
              }}>
                {filteredAlbums.map((album) => {
                  const links = Array.isArray(album.externalLinks) && album.externalLinks.length > 0
                    ? album.externalLinks.filter(l => l.url && l.url.trim() !== '')
                    : album.externalLink
                      ? [{ label: 'Google Photos', url: album.externalLink }]
                      : [];

                  return (
                    <div
                      key={album.id}
                      onClick={() => handleAlbumClick(album)}
                      style={{
                        background: '#fff',
                        borderRadius: '16px',
                        overflow: 'hidden',
                        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        position: 'relative',
                        border: '1px solid #f1f5f9',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-8px)';
                        e.currentTarget.style.boxShadow = '0 25px 50px -12px rgba(0, 0, 0, 0.15)';
                        const img = e.currentTarget.querySelector('.album-cover-image');
                        if (img) img.style.transform = 'scale(1.08)';
                        const title = e.currentTarget.querySelector('.album-title');
                        if (title) title.style.color = '#2563eb';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)';
                        const img = e.currentTarget.querySelector('.album-cover-image');
                        if (img) img.style.transform = 'scale(1)';
                        const title = e.currentTarget.querySelector('.album-title');
                        if (title) title.style.color = '#0f172a';
                      }}
                    >
                      {/* Cover Image & Overlays */}
                      <div style={{ height: '220px', overflow: 'hidden', background: '#f1f5f9', position: 'relative' }}>
                        {album.imageUrl ? (
                          <img
                            src={album.imageUrl}
                            alt={album.name}
                            style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.5s ease' }}
                            className="album-cover-image"
                          />
                        ) : (
                          <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#cbd5e1' }}>
                            <FaImages style={{ fontSize: '3rem' }} />
                          </div>
                        )}

                        {/* Top Left: Category Badge */}
                        <div style={{
                          position: 'absolute',
                          top: '16px',
                          left: '16px',
                          zIndex: 10,
                          background: '#3b82f6',
                          color: '#fff',
                          padding: '6px 14px',
                          borderRadius: '8px',
                          fontSize: '0.7rem',
                          fontWeight: '800',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                        }}>
                          {album.category || 'Umum'}
                        </div>

                        {/* Top Right: Count / Photo Badge */}
                        <div style={{
                          position: 'absolute',
                          top: '16px',
                          right: '16px',
                          zIndex: 10,
                          background: 'rgba(255, 255, 255, 0.95)',
                          backdropFilter: 'blur(8px)',
                          padding: '6px 12px',
                          borderRadius: '8px',
                          fontSize: '0.75rem',
                          fontWeight: '700',
                          color: '#1e293b',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                        }}>
                          <FaExternalLinkAlt style={{ fontSize: '0.7rem', color: '#3b82f6' }} />
                          {links.length > 1 ? `${links.length} Link Foto` : 'Google Photos'}
                        </div>

                        {/* Bottom Left: Date Badge */}
                        <div style={{
                          position: 'absolute',
                          bottom: '16px',
                          left: '16px',
                          zIndex: 10,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          color: '#fff',
                          fontSize: '0.75rem',
                          fontWeight: '600',
                          textShadow: '0 1px 3px rgba(0,0,0,0.8)'
                        }}>
                          <FaCalendarAlt style={{ color: '#fff', fontSize: '0.85rem' }} />
                          <span>
                            {album.date ? new Date(album.date).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' }) : '-'}
                          </span>
                        </div>
                      </div>

                      {/* Content */}
                      <div style={{ padding: '24px', flex: '1', display: 'flex', flexDirection: 'column' }}>
                        <h3 style={{
                          margin: '0 0 12px 0',
                          fontSize: '1.2rem',
                          fontWeight: '800',
                          color: '#0f172a',
                          lineHeight: '1.4',
                          transition: 'color 0.3s ease'
                        }} className="album-title">
                          {album.name}
                        </h3>

                        <p style={{
                          margin: '0',
                          fontSize: '0.9rem',
                          color: '#475569',
                          lineHeight: '1.6',
                          overflow: 'hidden',
                          display: '-webkit-box',
                          WebkitLineClamp: '2',
                          WebkitBoxOrient: 'vertical'
                        }}>
                          {album.description || 'Tidak ada deskripsi.'}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '80px 20px', background: '#fff', borderRadius: '16px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)' }}>
                <FaSearch style={{ fontSize: '3rem', color: '#e2e8f0', marginBottom: '20px' }} />
                <h3 style={{ color: '#1e293b', marginBottom: '8px' }}>Tidak ada album ditemukan</h3>
                <p style={{ color: '#64748b', maxWidth: '400px', margin: '0 auto 24px' }}>
                  Coba ganti kata kunci pencarian, filter tahun, atau kategori untuk menemukan album kegiatan lainnya.
                </p>
                <button
                  onClick={() => { setSearchQuery(''); setSelectedYear('Semua Tahun'); setSelectedCategory('Semua'); }}
                  style={{
                    background: '#3b82f6',
                    color: '#fff',
                    border: 'none',
                    padding: '10px 24px',
                    borderRadius: '8px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.background = '#2563eb'}
                  onMouseLeave={(e) => e.target.style.background = '#3b82f6'}
                >
                  Lihat Semua Album
                </button>
              </div>
            )}

          </div>
        </section>
      </AnimatedSection>

      {/* POPUP SELECTION MODAL UNTUK MULTI-LINK (DAY 1, DAY 2, DAY 3) */}
      {selectedAlbumForModal && createPortal(
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(15, 23, 42, 0.75)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          zIndex: 999999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }} onClick={() => setSelectedAlbumForModal(null)}>
          <div style={{
            background: '#ffffff',
            borderRadius: '24px',
            maxWidth: '480px',
            width: '100%',
            overflow: 'hidden',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.35)',
            position: 'relative',
            zIndex: 1000000
          }} onClick={(e) => e.stopPropagation()}>
            
            {/* Header Banner with Cover Image */}
            <div style={{
              height: '140px',
              position: 'relative',
              background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
              overflow: 'hidden'
            }}>
              {selectedAlbumForModal.imageUrl ? (
                <img
                  src={selectedAlbumForModal.imageUrl}
                  alt={selectedAlbumForModal.name}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', filter: 'brightness(0.7)' }}
                />
              ) : (
                <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0.3 }}>
                  <FaImages style={{ fontSize: '4rem', color: '#fff' }} />
                </div>
              )}

              {/* Close Button on Header */}
              <button
                onClick={() => setSelectedAlbumForModal(null)}
                style={{
                  position: 'absolute',
                  top: '16px',
                  right: '16px',
                  background: 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(4px)',
                  border: 'none',
                  borderRadius: '50%',
                  width: '36px',
                  height: '36px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: '#1e293b',
                  fontSize: '1rem',
                  boxShadow: '0 4px 10px rgba(0,0,0,0.15)',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#ef4444'; e.currentTarget.style.color = '#fff'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.9)'; e.currentTarget.style.color = '#1e293b'; }}
              >
                <FaTimes />
              </button>

              {/* Badge on Banner */}
              <div style={{
                position: 'absolute',
                bottom: '16px',
                left: '20px',
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(8px)',
                padding: '4px 12px',
                borderRadius: '20px',
                fontSize: '0.75rem',
                fontWeight: '800',
                color: '#2563eb',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
              }}>
                <FaExternalLinkAlt style={{ fontSize: '0.7em' }} /> {selectedAlbumForModal.links.length} Hari Dokumentasi
              </div>
            </div>

            {/* Modal Body Content */}
            <div style={{ padding: '24px 28px 28px' }}>
              <h3 style={{ margin: '0 0 6px 0', fontSize: '1.35rem', fontWeight: '800', color: '#0f172a', lineHeight: '1.3' }}>
                {selectedAlbumForModal.name}
              </h3>
              <p style={{ margin: '0 0 20px 0', fontSize: '0.875rem', color: '#64748b', lineHeight: '1.5' }}>
                Silakan pilih hari atau sesi dokumentasi foto yang ingin Anda buka:
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {selectedAlbumForModal.links.map((linkItem, idx) => (
                  <a
                    key={idx}
                    href={linkItem.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setSelectedAlbumForModal(null)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '14px',
                      padding: '14px 18px',
                      background: '#f8fafc',
                      border: '1.5px solid #e2e8f0',
                      borderRadius: '14px',
                      color: '#1e293b',
                      textDecoration: 'none',
                      transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#ffffff';
                      e.currentTarget.style.borderColor = '#2563eb';
                      e.currentTarget.style.boxShadow = '0 10px 25px -5px rgba(37, 99, 235, 0.15)';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      const iconCircle = e.currentTarget.querySelector('.link-icon-circle');
                      if (iconCircle) { iconCircle.style.background = '#2563eb'; iconCircle.style.color = '#fff'; }
                      const arrow = e.currentTarget.querySelector('.arrow-icon');
                      if (arrow) arrow.style.transform = 'translateX(4px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#f8fafc';
                      e.currentTarget.style.borderColor = '#e2e8f0';
                      e.currentTarget.style.boxShadow = 'none';
                      e.currentTarget.style.transform = 'translateY(0)';
                      const iconCircle = e.currentTarget.querySelector('.link-icon-circle');
                      if (iconCircle) { iconCircle.style.background = '#eff6ff'; iconCircle.style.color = '#2563eb'; }
                      const arrow = e.currentTarget.querySelector('.arrow-icon');
                      if (arrow) arrow.style.transform = 'translateX(0)';
                    }}
                  >
                    <div
                      className="link-icon-circle"
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '12px',
                        background: '#eff6ff',
                        color: '#2563eb',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1rem',
                        fontWeight: '800',
                        transition: 'all 0.2s ease',
                        flexShrink: 0
                      }}
                    >
                      {idx + 1}
                    </div>

                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '0.95rem', fontWeight: '800', color: '#0f172a' }}>
                        {linkItem.label || `Dokumentasi Day ${idx + 1}`}
                      </div>
                      <div style={{ fontSize: '0.775rem', color: '#64748b', marginTop: '2px' }}>
                        Buka Album Google Photos ↗
                      </div>
                    </div>

                    <div
                      className="arrow-icon"
                      style={{
                        color: '#2563eb',
                        fontSize: '0.9rem',
                        transition: 'transform 0.2s ease',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      <FaExternalLinkAlt />
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default GaleriPage;